import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'
import { Form, Formik } from 'formik'
import { map, omit } from 'lodash'
import Button from 'components/Button'
import { addModal } from 'features/modals/modalSlice'
import { showErrorNotification } from 'features/notifications/notificationsSlice'
import { colors, yupErrorToErrorObject, _ } from 'utils'
import ImportInput from './ImportInput'
import MatchingFieldsTable from './MatchingFieldsTable'
import { processData } from './utils'
import ValidationErrorList from './ValidationErrorList'
import ImportProgress from './ImportProgress'
import { setProgress } from './importExportSlice'
import { ImportDataFormProps } from './types'

const S = {
    DownloadExample: styled.p`
        margin-bottom: 32px;
        font-weight: 300;
        font-size: 16px;
        color: ${colors.grayDescription};
    `
}

const ImportDataForm = (importProps: ImportDataFormProps) => {
    const {
        mappedFields, getGroupMappedFields, sampleFile, validationSchema, onSubmit, close
    } = importProps
    const [step, setStep] = useState(1)
    const [errorFields, setErrorFields] = useState([])
    const dispatch = useDispatch()

    const handleSubmit = async (values) => {
        if (step === 1) {
            setStep(2)
            return
        }

        // Match fields
        const requireFields = mappedFields.filter((field) => field.isRequired).map((field) => field.field)
        const _errorFields = []
        requireFields.forEach((field) => {
            if (!values[field]) {
                _errorFields.push(field)
            }
        })
        setErrorFields(_errorFields)
        if (_errorFields.length) {
            dispatch(showErrorNotification('import_file.matching_field.missing_required_fields_error'))
            return
        }

        // Process and Validate Data
        const data = processData(
            values.importedColumns,
            values.importedData,
            omit(values, ['importedColumns', 'importedData', 'importFile']),
        )
        try {
            const validationResult = await validationSchema.validate(data, { abortEarly: false })
            onSubmit(validationResult)
            close()
            dispatch(setProgress({
                total: values.importedData.length, completed: 0, errorItems: []
            }))
            dispatch(addModal({
                Component: ({ close: uploadingClose }: { close: () => void }) => (
                    <ImportProgress close={uploadingClose} />
                ),
                backdrop: 'static'
            }))
        } catch (e) {
            const errorObject = yupErrorToErrorObject(e)
            const errors = map(errorObject, (value, key) => {
                const [row, field] = key.split('.')
                const rowNum = (row || '').replace(/[^0-9]/g, '')
                return {
                    row: rowNum,
                    field: mappedFields.find((f) => f.field === field)?.label,
                    detail: _(value[0])
                }
            })
            dispatch(addModal({
                header: _('import_file.error_list_header'),
                Component: ({ close: errorClose }: { close: () => void }) => (
                    <ValidationErrorList
                        errorsFE={errors}
                        errorClose={errorClose}
                        importProps={importProps}
                        onSubmit={onSubmit}
                    />
                ),
                backdrop: 'static'
            }))
        }
    }
    return (
        <Formik initialValues={{ importedData: [], importedColumns: [] }} onSubmit={handleSubmit}>
            {({ values }) => (
                <Form>
                    {step === 1 && (
                        <>
                            <S.DownloadExample>
                                {_('import_file.download_example_description')}
                                {',  '}
                                <a
                                    href={sampleFile}
                                    target="_blank"
                                    rel="noreferrer"
                                    style={{ fontWeight: 400 }}
                                >
                                    {_('import_file.download_example_here', {}, false)}
                                </a>
                            </S.DownloadExample>
                            <ImportInput />
                        </>
                    )}
                    {step === 2 && (
                        <MatchingFieldsTable
                            groups={getGroupMappedFields?.()}
                            fields={mappedFields}
                            errorFields={errorFields}
                            columns={values?.importedColumns}
                        />
                    )}
                    <div className="flex-space-between mt-4">
                        <div />
                        <div>
                            <Button variant="secondary" className="mr-2" onClick={close}>
                                {_('button.cancel')}
                            </Button>
                            <Button
                                variant="primary"
                                onClick={() => handleSubmit(values)}
                                disabled={step === 1 && !values?.importedData?.length}
                            >
                                {_('button.continue')}
                            </Button>
                        </div>
                    </div>
                </Form>
            )}
        </Formik>
    )
}

export default ImportDataForm
